import React, { useState, useEffect } from 'react';
import '../styles/StreakDisplay.css';

const StreakDisplay = ({ currentStreak, isCompletedToday, onStreakClick, previousStreak, isActive }) => {
  const [streakDays, setStreakDays] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayStreak, setDisplayStreak] = useState(currentStreak);

  // Generate streak days (last 7 days)
  useEffect(() => {
    const days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      const isToday = i === 0;
      const isCompleted = isToday ? isCompletedToday : (currentStreak > i);
      const isActive = isToday && isCompleted;
      
      days.push({
        date,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        isCompleted,
        isActive,
        isToday
      });
    }
    
    setStreakDays(days);
  }, [currentStreak, isCompletedToday]);

  // Update display streak and trigger animation when streak changes
  useEffect(() => {
    if (isCompletedToday && currentStreak > displayStreak) {
      // User just completed today's task - show glowy effect
      setIsAnimating(true);
      setDisplayStreak(currentStreak);
      const timer = setTimeout(() => setIsAnimating(false), 3000);
      return () => clearTimeout(timer);
    } else if (!isActive && previousStreak > 0) {
      // Show previous day's streak (grayed out) when inactive
      setDisplayStreak(previousStreak);
    } else if (isActive && currentStreak > 0) {
      // Show current active streak
      setDisplayStreak(currentStreak);
    } else {
      setDisplayStreak(currentStreak);
    }
  }, [currentStreak, isCompletedToday, displayStreak, previousStreak, isActive]);

  // Determine if streak is "secure" (not about to be lost)
  const isStreakSecure = isActive && currentStreak > 0 && isCompletedToday;
  const isStreakInactive = !isActive && (previousStreak > 0 || (currentStreak === 0 && previousStreak > 0));

  return (
    <div className={`streak-display ${isStreakSecure ? 'secure' : isStreakInactive ? 'inactive' : ''}`} onClick={onStreakClick}>
      <div className="streak-header">
        <div className="streak-icon">
          {isStreakSecure ? (
            <div className={`fire-icon ${isAnimating ? 'glowing' : 'secure-glow'}`}>
              🔥
            </div>
          ) : isCompletedToday ? (
            <div className={`fire-icon ${isAnimating ? 'glowing' : ''}`}>
              🔥
            </div>
          ) : isStreakInactive ? (
            <div className="gray-fire-icon inactive-fire">🔥</div>
          ) : (
            <div className="gray-fire-icon">🔥</div>
          )}
        </div>
        <div className="streak-info">
          <div className={`streak-number ${isStreakInactive ? 'inactive-number' : ''}`}>
            {displayStreak}
          </div>
          <div className="streak-label">
            {isStreakInactive ? 'day streak (inactive)' : 'day streak'}
          </div>
        </div>
      </div>
      
      <div className="streak-calendar">
        {streakDays.map((day, index) => (
          <div
            key={index}
            className={`streak-day ${day.isCompleted ? 'completed' : 'incomplete'} ${day.isActive ? 'active' : ''} ${day.isToday ? 'today' : ''}`}
          >
            <div className="day-name">{day.dayName}</div>
            <div className="day-icon">
              {day.isCompleted ? (
                <div className={`fire-small ${day.isActive ? 'glowing' : ''}`}>
                  🔥
                </div>
              ) : (
                <div className="gray-fire-small">🔥</div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {isCompletedToday && (
        <div className="streak-message">
          <span className="streak-encouragement">Keep it up!</span>
        </div>
      )}
      
      {isStreakInactive && (
        <div className="streak-message">
          <span className="streak-warning">Complete a task to continue your streak!</span>
        </div>
      )}
    </div>
  );
};

export default StreakDisplay;
